<script setup>
import { computed, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const detail = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const page = ref(1);
const status = ref('');
const reason = ref('');
const reasons = [
  'template_cooldown_24h',
  'blocked_contact',
  'no_phone_number',
  'invalid_template',
  'provider_failure',
  'retry_success',
  'permanent_failure',
];
const query = ref('');
const isExporting = ref(false);
const exportError = ref(false);
let requestId = 0;
let exportRequestId = 0;
const invalidate = () => {
  requestId += 1;
  exportRequestId += 1;
  isExporting.value = false;
};
onBeforeUnmount(invalidate);
const statuses = [
  'pending',
  'skipped',
  'enqueued',
  'sent_to_provider',
  'provider_failure',
  'retry_success',
  'permanent_failure',
  'sent',
  'delivered',
  'read',
  'failed',
  'no_sent',
];

const METRICS = [
  'enviados',
  'entregados',
  'leidos',
  'fallidos',
  'respondio',
  'no_enviado',
];
const OUTCOME_METRICS = [
  'audience_total',
  'acceptance_unknown',
  'eligible',
  'enqueued',
  'sent_to_provider',
  'delivered',
  'read',
  'failed',
  'provider_failure',
  'retries_scheduled',
  'retry_success',
  'permanent_failure',
  'template_cooldown_24h',
  'blocked_contact',
  'no_phone_number',
  'invalid_template',
  'duplicates_avoided',
  'pending_or_failed',
];
const RATES = ['tasa_entregados', 'tasa_leidos', 'tasa_respondio'];
const detailMetrics = computed(() =>
  detail.value?.metrics?.legacy ? METRICS : OUTCOME_METRICS
);

const num = value => (value == null ? '—' : Number(value));

const statusLabel = value =>
  t(`REPORT.CAMPAIGN_PERFORMANCE.RECIPIENT_STATUS.${value}`);

const reasonLabel = value =>
  value ? t(`REPORT.CAMPAIGN_PERFORMANCE.RECIPIENT_REASON.${value}`) : '—';

const effectiveStatusLabel = value =>
  t(`REPORT.CAMPAIGN_PERFORMANCE.EFFECTIVE_STATUS.${value}`);

const diagnosticCause = execution => {
  if (!execution?.last_error_code && !execution?.last_error_message) return '—';
  return [execution.last_error_code, execution.last_error_message]
    .filter(Boolean)
    .join(': ');
};

const formatDate = value => {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    timeZone: detail.value?.execution?.scheduled_timezone || 'UTC',
  });
};

const fetchDetail = async () => {
  if (!props.campaign?.id) return;
  requestId += 1;
  const currentRequest = requestId;
  const campaignId = props.campaign.id;
  detail.value = null;
  hasError.value = false;
  isLoading.value = true;
  try {
    const { data } = await ReportsAPI.getCampaignPerformanceDetail({
      id: campaignId,
      page: page.value,
      perPage: 50,
      status: status.value || undefined,
      reason: reason.value || undefined,
      query: query.value || undefined,
    });
    if (currentRequest === requestId && props.campaign?.id === campaignId)
      detail.value = data;
  } catch (error) {
    if (currentRequest === requestId) hasError.value = true;
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const open = () => {
  invalidate();
  page.value = 1;
  status.value = '';
  reason.value = '';
  query.value = '';
  exportError.value = false;
  dialogRef.value?.open();
  fetchDetail();
};

const filterRecipients = () => {
  page.value = 1;
  fetchDetail();
};
const changePage = delta => {
  page.value += delta;
  fetchDetail();
};

const downloadRecipients = async () => {
  const campaignId = props.campaign?.id;
  if (!campaignId || isExporting.value) return;
  exportRequestId += 1;
  const currentExport = exportRequestId;
  isExporting.value = true;
  exportError.value = false;
  try {
    const { data } = await ReportsAPI.getCampaignPerformanceExport({
      id: campaignId,
      status: status.value || undefined,
      reason: reason.value || undefined,
      query: query.value || undefined,
    });
    if (currentExport !== exportRequestId || props.campaign?.id !== campaignId)
      return;
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign-${campaignId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    if (currentExport === exportRequestId) exportError.value = true;
  } finally {
    if (currentExport === exportRequestId) isExporting.value = false;
  }
};

defineExpose({ open, dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="campaign?.title || ''"
    :description="t('REPORT.CAMPAIGN_PERFORMANCE.DIALOG_DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('REPORT.CAMPAIGN_PERFORMANCE.CLOSE')"
    @close="invalidate"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>

    <div
      v-else-if="hasError"
      class="p-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ t('REPORT.CAMPAIGN_PERFORMANCE.ERROR') }}
    </div>

    <div v-else-if="detail" class="flex flex-col gap-4">
      <p class="text-xs text-n-slate-11">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.UPDATED_AT') }}:
        {{ formatDate(detail.generated_at) || '—' }}
      </p>
      <div class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.TITLE') }}
        </span>
        <dl class="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          <div>
            <dt class="text-n-slate-11">
              {{
                t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.EFFECTIVE_STATUS')
              }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ effectiveStatusLabel(detail.execution.effective_status) }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.SCHEDULED_LOCAL') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ detail.execution.scheduled_at_local || '—' }}
              <span v-if="detail.execution.scheduled_timezone">
                ({{ detail.execution.scheduled_timezone }})
              </span>
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.SCHEDULED_UTC') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ detail.execution.scheduled_at_utc || '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.LAST_CHANGED') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ detail.execution.last_changed_at || '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.ATTEMPTS') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ num(detail.execution.attempt_count) }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.LAST_CAUSE') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ diagnosticCause(detail.execution) }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{
                t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.AUDIENCE_ESTIMATE')
              }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ num(detail.execution.audience_estimate) }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{
                t(
                  'REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.MATERIALIZED_RECIPIENTS'
                )
              }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ num(detail.execution.materialized_recipients) }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.DIAGNOSTICS.RECONCILIATION') }}
            </dt>
            <dd class="font-medium text-n-slate-12">
              {{ num(detail.execution.reconciliation_total) }} /
              {{ num(detail.execution.materialized_recipients) }} ·
              {{
                detail.execution.reconciled
                  ? t('REPORT.CAMPAIGN_PERFORMANCE.YES')
                  : t('REPORT.CAMPAIGN_PERFORMANCE.NO')
              }}
            </dd>
          </div>
        </dl>
      </div>

      <div class="grid grid-cols-3 gap-2 md:grid-cols-6">
        <div
          v-for="metric in detailMetrics"
          :key="metric"
          class="flex flex-col gap-0.5 p-2 border rounded-lg border-n-weak"
        >
          <span class="text-xs text-n-slate-11">
            {{ t(`REPORT.CAMPAIGN_PERFORMANCE.METRICS.${metric}`) }}
          </span>
          <span class="text-base font-medium tabular-nums text-n-slate-12">
            {{ num(detail.metrics[metric]) }}
          </span>
        </div>
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11">
        <span v-for="rate in RATES" :key="rate" class="whitespace-nowrap">
          {{ t(`REPORT.CAMPAIGN_PERFORMANCE.RATES.${rate}`) }}:
          <span class="font-medium text-n-slate-12">
            {{ num(detail.metrics[rate]) }}%
          </span>
        </span>
      </div>

      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS_HEADING') }}
        </span>
        <button
          type="button"
          class="flex items-center h-8 px-3 text-xs rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 disabled:opacity-50"
          :disabled="isExporting || !detail.recipients.length"
          @click="downloadRecipients"
        >
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.DOWNLOAD') }}
        </button>
      </div>

      <p class="text-xs text-n-slate-11">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.SEMANTICS') }}
      </p>
      <p class="text-xs text-n-slate-11">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.USERNAME_CURRENT_NOTE') }}
      </p>
      <p v-if="exportError" role="alert">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.ERROR') }}
      </p>
      <form class="flex flex-wrap gap-2" @submit.prevent="filterRecipients">
        <input
          v-model="query"
          :aria-label="t('REPORT.CAMPAIGN_PERFORMANCE.SEARCH')"
          :placeholder="t('REPORT.CAMPAIGN_PERFORMANCE.SEARCH')"
        />
        <select
          v-model="status"
          :aria-label="t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.STATUS')"
          @change="filterRecipients"
        >
          <option value="">
            {{ t('REPORT.CAMPAIGN_PERFORMANCE.ALL_STATUSES') }}
          </option>
          <option v-for="value in statuses" :key="value" :value="value">
            {{ statusLabel(value) }}
          </option>
        </select>
        <select
          v-model="reason"
          :aria-label="t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.REASON')"
          @change="filterRecipients"
        >
          <option value="">
            {{ t('REPORT.CAMPAIGN_PERFORMANCE.ALL_REASONS') }}
          </option>
          <option v-for="value in reasons" :key="value" :value="value">
            {{ reasonLabel(value) }}
          </option>
        </select>
        <button type="submit">
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.SEARCH') }}
        </button>
      </form>
      <div v-if="detail.meta" class="flex justify-between gap-2">
        <button :disabled="page <= 1" @click="changePage(-1)">
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.PREVIOUS') }}
        </button>
        <span
          >{{ page }} · {{ detail.meta.total }}
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS_HEADING') }}</span
        >
        <button
          :disabled="page * detail.meta.per_page >= detail.meta.total"
          @click="changePage(1)"
        >
          {{ t('REPORT.CAMPAIGN_PERFORMANCE.NEXT') }}
        </button>
      </div>
      <div class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm text-left">
          <thead class="text-n-slate-11 bg-n-alpha-black2">
            <tr>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.NAME') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.PHONE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{
                  t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.WHATSAPP_USERNAME')
                }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.TEMPLATE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.STATUS') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.REASON') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.RETRIES') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.RECIPIENTS.DATE') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="!detail.recipients.length">
              <td colspan="8" class="px-3 py-4 text-center text-n-slate-11">
                {{ t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.NO_DATA') }}
              </td>
            </tr>
            <tr
              v-for="recipient in detail.recipients"
              :key="
                recipient.id || recipient.message_id || recipient.contact_id
              "
              class="text-n-slate-12"
            >
              <td class="px-3 py-2 whitespace-nowrap">
                {{ recipient.name }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                {{ recipient.phone_number }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                {{ recipient.whatsapp_username || '—' }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                {{ recipient.template || '—' }}
              </td>
              <td class="px-3 py-2">
                {{
                  recipient.delivery_outcome === 'unknown'
                    ? t('REPORT.CAMPAIGN_PERFORMANCE.UNKNOWN_ACCEPTANCE')
                    : statusLabel(
                        recipient.delivery_status &&
                          ['delivered', 'read'].includes(
                            recipient.delivery_status
                          )
                          ? recipient.delivery_status
                          : recipient.status
                      )
                }}
              </td>
              <td class="px-3 py-2">{{ reasonLabel(recipient.reason) }}</td>
              <td class="px-3 py-2 tabular-nums">
                {{ num(recipient.retry_attempts) }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                {{ formatDate(recipient.status_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </Dialog>
</template>
